/**
 * AddJobDialog — Create-New-Job modal (extracted Feb 2026 from JobsEnhanced.jsx).
 *
 * Pure presentational: all state + handlers live in the parent. This keeps
 * the parent as the single source of truth for jobs/users/customers/etc.,
 * and lets the dialog be unit-testable in isolation.
 *
 * Trigger button + Dialog wrapper are bundled so the parent can drop in a
 * single <AddJobDialog ... /> wherever it used to have the inline form.
 */
import React, { useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Plus, MapPin, AlertCircle, Loader2, FileText, X, Camera, Clock, UserCog, Tag,
} from 'lucide-react';
import { COUNTRIES, getStatesForCountry, getPostalCodeLabel, getStateLabel } from '../../data/countries';
import JobLabelsEditor from '../JobLabelsEditor';
import WorkerMultiSelect, { teamLabel } from './WorkerMultiSelect';

export default function AddJobDialog({
  open,
  onOpenChange,
  // Form state
  newJob, setNewJob,
  // Assignment
  assignmentType, setAssignmentType,
  users, teams,
  // Customers
  customers,
  showAddCustomerInline, setShowAddCustomerInline,
  newCustomerName, setNewCustomerName,
  newCustomerEmail, setNewCustomerEmail,
  newCustomerPhone, setNewCustomerPhone,
  onAddCustomerInline,
  // Location
  locationMode, setLocationMode,
  gpsCoordinates, setGpsCoordinates,
  capturingGPS, onCaptureCurrentLocation,
  // PDF
  pdfFile, setPdfFile,
  // Labels (Aug 2026) — selected label_ids applied right after creation
  labelIds = [], setLabelIds,
  labelSuggestions = [],
  // Project link (Aug 2026) — optional project typeahead
  token,
  // Submit
  onSubmit, isLoading,
}) {
  const [projQuery, setProjQuery] = useState('');
  const [projResults, setProjResults] = useState([]);
  const [projOpen, setProjOpen] = useState(false);
  const API = `${window.__BACKEND_URL__}/api`;
  const searchProjects = async (q) => {
    try {
      const { data } = await axios.get(`${API}/projects/search?q=${encodeURIComponent(q)}&limit=20`,
        { headers: { Authorization: `Bearer ${token}` } });
      setProjResults(Array.isArray(data) ? data : []);
    } catch { setProjResults([]); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="bg-[#1E88E5] hover:from-blue-700 hover:to-green-700 text-white font-semibold" data-testid="open-add-job-btn">
          <Plus className="w-5 h-5 mr-2" />
          Add Job
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-[95vw] sm:max-w-2xl h-auto max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-xl sm:text-2xl">Create New Job</DialogTitle>
          <DialogDescription className="text-sm">
            Assign a new job to a team, a squad, or one or more workers
          </DialogDescription>
        </DialogHeader>
        <form id="add-job-form" onSubmit={onSubmit} className="space-y-4 sm:space-y-5 overflow-y-auto flex-1 pr-2" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="space-y-2">
            <Label htmlFor="title">Job Title *</Label>
            <Input
              id="title"
              placeholder="Enter job title"
              value={newJob.title}
              onChange={(e) => setNewJob({ ...newJob, title: e.target.value })}
              required
              className="h-11"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Enter job description"
              value={newJob.description}
              onChange={(e) => setNewJob({ ...newJob, description: e.target.value })}
              rows={4}
            />
          </div>
          {/* Assignment Type Selector */}
          <div className="space-y-4 p-4 bg-[#0F1923] rounded-lg border-2 border-[#2D3748]">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <UserCog className="w-5 h-5 text-[#00C896]" />
                <Label className="text-sm font-semibold text-green-900">
                  Assignment Type
                </Label>
              </div>
              <div className="flex space-x-2">
                <Button
                  type="button"
                  onClick={() => setAssignmentType('user')}
                  variant={assignmentType === 'user' ? 'default' : 'outline'}
                  size="sm"
                  className={assignmentType === 'user' ? 'bg-[#00C896]' : ''}
                  data-testid="assign-type-user"
                >
                  Worker(s)
                </Button>
                <Button
                  type="button"
                  onClick={() => setAssignmentType('team')}
                  variant={assignmentType === 'team' ? 'default' : 'outline'}
                  size="sm"
                  className={assignmentType === 'team' ? 'bg-[#1E88E5]' : ''}
                  data-testid="assign-type-team"
                >
                  Team / Squad
                </Button>
              </div>
            </div>

            {assignmentType === 'user' ? (
              <div className="space-y-2">
                <Label>Assign to worker(s) *</Label>
                <WorkerMultiSelect
                  users={users}
                  value={newJob.assigned_user_ids || []}
                  onChange={(ids) => setNewJob({ ...newJob, assigned_user_ids: ids, assigned_to: ids[0] || '', assigned_team_id: '' })}
                  testId="add-job-workers"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="assigned_team_id">Assign to team or squad *</Label>
                <Select
                  value={newJob.assigned_team_id}
                  onValueChange={(value) => setNewJob({ ...newJob, assigned_team_id: value, assigned_to: '' })}
                  required
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Select team" />
                  </SelectTrigger>
                  <SelectContent>
                    {teams.map((team) => (
                      <SelectItem key={team.id} value={team.id} data-testid={`add-job-team-option-${team.id}`}>
                        {teamLabel(team)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="customer_select">Customer <span className="text-red-500">*</span></Label>
              {showAddCustomerInline ? (
                <div className="space-y-2 p-3 bg-[#0F1923] rounded-lg border border-[#2D3748]">
                  <Input
                    placeholder="Customer name *"
                    value={newCustomerName}
                    onChange={(e) => setNewCustomerName(e.target.value)}
                    className="h-10"
                    data-testid="new-customer-name"
                  />
                  <Input
                    placeholder="Email (optional)"
                    value={newCustomerEmail}
                    onChange={(e) => setNewCustomerEmail(e.target.value)}
                    className="h-10"
                    data-testid="new-customer-email"
                  />
                  <Input
                    placeholder="Phone (optional)"
                    value={newCustomerPhone}
                    onChange={(e) => setNewCustomerPhone(e.target.value)}
                    className="h-10"
                    data-testid="new-customer-phone"
                  />
                  <div className="flex gap-2">
                    <Button type="button" size="sm" onClick={onAddCustomerInline} data-testid="save-new-customer-btn">Save</Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => setShowAddCustomerInline(false)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Select
                    value={newJob.customer_id}
                    onValueChange={(value) => {
                      if (value === '__add_new__') {
                        setShowAddCustomerInline(true);
                        return;
                      }
                      const cust = customers.find((c) => c.id === value);
                      setNewJob({
                        ...newJob,
                        customer_id: value,
                        customer_name: cust?.name || '',
                        customer_email: cust?.email || '',
                      });
                    }}
                    required
                  >
                    <SelectTrigger className="h-11" data-testid="customer-select">
                      <SelectValue placeholder="Select customer (required)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__add_new__">+ Add New Customer</SelectItem>
                      {customers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {newJob.customer_id && (
                    <Button type="button" variant="ghost" size="sm" className="text-xs text-slate-500"
                      onClick={() => setNewJob({ ...newJob, customer_id: '', customer_name: '', customer_email: '' })}>
                      Clear customer
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Scheduled Dates */}
            <div className="space-y-2">
              <Label htmlFor="scheduled_start">Scheduled Start <span className="text-red-500">*</span></Label>
              <Input
                id="scheduled_start"
                type="datetime-local"
                value={newJob.scheduled_start}
                onChange={(e) => setNewJob({ ...newJob, scheduled_start: e.target.value })}
                className="h-11"
                required
                data-testid="scheduled-start"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="scheduled_completion">Scheduled Completion <span className="text-red-500">*</span></Label>
              <Input
                id="scheduled_completion"
                type="datetime-local"
                value={newJob.scheduled_completion}
                onChange={(e) => setNewJob({ ...newJob, scheduled_completion: e.target.value })}
                className="h-11"
                required
                data-testid="scheduled-completion"
              />
            </div>
          </div>

          {/* Billing Rate */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="service_rate">Billing Rate ($/hr) <span className="text-red-500">*</span></Label>
              <Input
                id="service_rate"
                type="number"
                step="0.01"
                min="0"
                placeholder="e.g. 65"
                value={newJob.service_rate}
                onChange={(e) => setNewJob({ ...newJob, service_rate: e.target.value })}
                className="h-11"
                required
                data-testid="service-rate"
              />
            </div>
          </div>

          {/* Project link (Aug 2026) — optional, keeps project reports complete */}
          <div className="space-y-2 relative">
            <Label htmlFor="add_project">Project (optional)</Label>
            <Input
              id="add_project"
              placeholder="Search projects by number or name…"
              autoComplete="off"
              data-testid="add-project-input"
              value={newJob.project_id
                ? `${newJob.project_number || ''}${newJob.project_name ? ` — ${newJob.project_name}` : ''}`
                : projQuery}
              onFocus={() => { setProjOpen(true); searchProjects(''); }}
              onBlur={() => setTimeout(() => setProjOpen(false), 150)}
              onChange={(e) => {
                const raw = e.target.value;
                setNewJob({ ...newJob, project_id: '', project_number: '', project_name: '' });
                setProjQuery(raw);
                setProjOpen(true);
                searchProjects(raw.split(' ')[0].trim());
              }}
              className="h-11"
            />
            {newJob.project_id && (
              <button
                type="button"
                data-testid="add-project-clear"
                onClick={() => { setNewJob({ ...newJob, project_id: '', project_number: '', project_name: '' }); setProjQuery(''); }}
                className="absolute right-3 top-9 text-xs text-slate-400 hover:text-slate-200"
              >
                Clear
              </button>
            )}
            {projOpen && projResults.length > 0 && (
              <div
                data-testid="add-project-results"
                className="absolute z-50 left-0 right-0 mt-1 bg-[#1A2433] border border-[#2D3748] rounded-md shadow-lg max-h-56 overflow-y-auto"
              >
                {projResults.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`add-project-pick-${p.id}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setNewJob({ ...newJob, project_id: p.id, project_number: p.project_number, project_name: p.name });
                      setProjQuery('');
                      setProjOpen(false);
                    }}
                    className="w-full px-3 py-2 text-left hover:bg-[#0F1923] border-b last:border-b-0 border-[#2D3748]"
                  >
                    <div className="font-mono text-xs text-emerald-400">{p.project_number}</div>
                    <div className="text-sm text-white">{p.name}</div>
                    {p.customer_name && (
                      <div className="text-[11px] text-slate-400">{p.customer_name}</div>
                    )}
                  </button>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-500">Linking a project makes it show up in the Hours by Project report and QuickBooks invoices.</p>
          </div>

          {/* Customer Reference Numbers (Feb 2026 — flows to QuickBooks
              when invoicing). PO Number is QBO's native Invoice.PONumber
              field; Work Order Number rides in PrivateNote. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="customer_po_number">PO Number (from customer)</Label>
              <Input
                id="customer_po_number"
                placeholder="e.g. PO-44512"
                maxLength={25}
                value={newJob.customer_po_number || ''}
                onChange={(e) => setNewJob({ ...newJob, customer_po_number: e.target.value })}
                className="h-11"
                data-testid="add-job-po-number"
              />
              <p className="text-xs text-gray-500">Pushed to QuickBooks Invoice as the customer-visible PO #.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="work_order_number">Work Order #</Label>
              <Input
                id="work_order_number"
                placeholder="e.g. WO-7788"
                maxLength={64}
                value={newJob.work_order_number || ''}
                onChange={(e) => setNewJob({ ...newJob, work_order_number: e.target.value })}
                className="h-11"
                data-testid="add-job-work-order"
              />
              <p className="text-xs text-gray-500">Included in the QBO invoice memo when you push.</p>
            </div>
          </div>

          {/* Labels (Aug 2026) — pick from the company label catalog while
              creating the job; they're applied the moment the job is saved. */}
          <div className="p-4 bg-[#0F1923] rounded-lg border-2 border-[#2D3748] space-y-2" data-testid="add-job-labels-section">
            <div className="flex items-center space-x-2">
              <Tag className="w-5 h-5 text-[#1E88E5]" />
              <Label className="text-sm font-semibold">Labels</Label>
            </div>
            <p className="text-xs text-gray-500">
              Optional: tag this job (e.g. HVAC, Emergency, Contract) for filtering, search and label billing rates.
            </p>
            <JobLabelsEditor
              jobId={null}
              value={labelIds}
              canEdit
              canManageCatalog
              onChange={(ids) => setLabelIds?.(ids)}
              emptyHint="No labels"
            />
            {labelSuggestions.filter((s) => !labelIds.includes(s.id)).length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 pt-1" data-testid="label-suggestions-row">
                <span className="text-[11px]" style={{ color: '#64748B' }}>Often used for this customer:</span>
                {labelSuggestions.filter((s) => !labelIds.includes(s.id)).map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setLabelIds?.([...labelIds, s.id])}
                    className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide transition-all active:scale-95"
                    style={{
                      background: `${s.color || '#1E88E5'}1A`,
                      color: s.color || '#94A3B8',
                      border: `1px dashed ${s.color || '#1E88E5'}66`,
                    }}
                    data-testid={`label-suggestion-${s.id}`}
                  >
                    + {s.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Location Mode Selector */}
          <div className="space-y-4 p-4 bg-[#0F1923] rounded-lg border-2 border-[#2D3748]">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <MapPin className="w-5 h-5 text-[#1E88E5]" />
                <Label className="text-sm font-semibold text-blue-900">
                  Job Location
                </Label>
              </div>
              <div className="flex space-x-2">
                <Button
                  type="button"
                  onClick={() => setLocationMode('address')}
                  variant={locationMode === 'address' ? 'default' : 'outline'}
                  size="sm"
                  className={locationMode === 'address' ? 'bg-[#1E88E5]' : ''}
                >
                  Address
                </Button>
                <Button
                  type="button"
                  onClick={() => setLocationMode('gps')}
                  variant={locationMode === 'gps' ? 'default' : 'outline'}
                  size="sm"
                  className={locationMode === 'gps' ? 'bg-[#00C896]' : ''}
                >
                  GPS Coordinates
                </Button>
              </div>
            </div>

            {/* GPS Verification Toggle (Admin/Supervisor only) */}
            <div className="flex items-center space-x-3 p-3 bg-[#0F1923] rounded-lg border border-[#FFA000]">
              <input
                type="checkbox"
                id="location_verification"
                checked={newJob.location_verification_required}
                onChange={(e) => setNewJob({ ...newJob, location_verification_required: e.target.checked })}
                className="w-5 h-5 rounded border-[#2D3748] text-[#1E88E5] focus:ring-blue-500"
              />
              <div className="flex-1">
                <Label htmlFor="location_verification" className="text-sm font-semibold text-yellow-900 cursor-pointer">
                  Require GPS Location Verification
                </Label>
                <p className="text-xs text-yellow-700 mt-1">
                  {newJob.location_verification_required
                    ? 'Worker must be within 500m of job site to start work'
                    : 'Worker can start job from anywhere (useful for office work, admin tasks, or when location is not critical)'}
                </p>
              </div>
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
            </div>

            {locationMode === 'address' ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="street_address">Street Address *</Label>
                  <Input
                    id="street_address"
                    placeholder="e.g., 123 Main Street"
                    value={newJob.street_address}
                    onChange={(e) => setNewJob({ ...newJob, street_address: e.target.value })}
                    className="h-11"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      placeholder="City"
                      value={newJob.city}
                      onChange={(e) => setNewJob({ ...newJob, city: e.target.value })}
                      className="h-11"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="country">Country *</Label>
                    <Select
                      value={newJob.country}
                      onValueChange={(value) => setNewJob({ ...newJob, country: value, state_province: '' })}
                      required
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="state_province">{getStateLabel(newJob.country)} *</Label>
                    <Select
                      value={newJob.state_province}
                      onValueChange={(value) => setNewJob({ ...newJob, state_province: value })}
                      required
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder={`Select ${getStateLabel(newJob.country).toLowerCase()}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {getStatesForCountry(newJob.country).map((state) => (
                          <SelectItem key={state} value={state}>
                            {state}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="postal_code">{getPostalCodeLabel(newJob.country)} *</Label>
                    <Input
                      id="postal_code"
                      placeholder={getPostalCodeLabel(newJob.country)}
                      value={newJob.postal_code}
                      onChange={(e) => setNewJob({ ...newJob, postal_code: e.target.value })}
                      className="h-11"
                      required
                    />
                  </div>
                </div>

                <p className="text-xs text-blue-700">
                  Workers will be verified at this location when they start the job (500m radius).
                </p>
              </>
            ) : (
              <>
                <div className="space-y-4">
                  <p className="text-xs text-green-700 font-medium">
                    Use GPS coordinates for remote locations (forestry sites, mines, processing plants, etc.)
                  </p>

                  <div className="space-y-2">
                    <Label htmlFor="gps_description">Location Description</Label>
                    <Input
                      id="gps_description"
                      placeholder="e.g., Forestry Site North, Mining Camp 3"
                      value={gpsCoordinates.description}
                      onChange={(e) => setGpsCoordinates({ ...gpsCoordinates, description: e.target.value })}
                      className="h-11"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="latitude">Latitude *</Label>
                      <Input
                        id="latitude"
                        type="number"
                        step="0.000001"
                        placeholder="e.g., 49.282729"
                        value={gpsCoordinates.latitude}
                        onChange={(e) => setGpsCoordinates({ ...gpsCoordinates, latitude: e.target.value })}
                        className="h-11"
                        required={locationMode === 'gps'}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="longitude">Longitude *</Label>
                      <Input
                        id="longitude"
                        type="number"
                        step="0.000001"
                        placeholder="e.g., -123.120738"
                        value={gpsCoordinates.longitude}
                        onChange={(e) => setGpsCoordinates({ ...gpsCoordinates, longitude: e.target.value })}
                        className="h-11"
                        required={locationMode === 'gps'}
                      />
                    </div>
                  </div>

                  <Button
                    type="button"
                    onClick={onCaptureCurrentLocation}
                    disabled={capturingGPS}
                    variant="outline"
                    className="w-full border-[#00C896] text-green-700 hover:bg-[#0F1923]"
                  >
                    {capturingGPS ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Capturing Location...
                      </>
                    ) : (
                      <>
                        <MapPin className="w-4 h-4 mr-2" />
                        Use My Current Location
                      </>
                    )}
                  </Button>

                  {gpsCoordinates.latitude && gpsCoordinates.longitude && (
                    <div className="p-3 bg-[#1A2433] rounded border border-[#00C896]">
                      <p className="text-xs text-green-900 font-medium">
                        📍 Location: {gpsCoordinates.latitude}, {gpsCoordinates.longitude}
                      </p>
                      <p className="text-xs text-green-700 mt-1">
                        Workers will be verified within 500m radius of these coordinates
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* PDF Attachment Section */}
          <div className="p-4 bg-[#0F1923] rounded-lg border-2 border-[#2D3748] space-y-3">
            <div className="flex items-center space-x-2">
              <FileText className="w-5 h-5 text-[#A78BFA]" />
              <Label className="text-sm font-semibold text-purple-900">
                Attach Instructions/Safety Procedures (PDF)
              </Label>
            </div>
            <p className="text-xs text-purple-700">
              Optional: Upload a PDF file with job instructions, safety procedures, or reference documents
            </p>
            <div className="flex items-center space-x-2">
              <Input
                type="file"
                accept=".pdf"
                onChange={(e) => setPdfFile(e.target.files[0])}
                className="h-10 flex-1"
              />
              {pdfFile && (
                <Button
                  type="button"
                  onClick={() => setPdfFile(null)}
                  variant="ghost"
                  size="sm"
                  className="text-[#EF4444] hover:text-red-700"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
            {pdfFile && (
              <div className="text-xs text-purple-900 bg-[#1A2433] p-2 rounded border border-[#2D3748]">
                Selected: {pdfFile.name} ({(pdfFile.size / 1024 / 1024).toFixed(2)} MB)
              </div>
            )}
          </div>

          <div className="flex items-center space-x-3 p-4 bg-[#0F1923] rounded-lg border border-orange-200">
            <input
              type="checkbox"
              id="photos_required"
              checked={newJob.photos_required}
              onChange={(e) => setNewJob({ ...newJob, photos_required: e.target.checked })}
              className="w-5 h-5 rounded border-[#2D3748] text-[#FFA000] focus:ring-orange-500"
            />
            <div className="flex-1">
              <Label htmlFor="photos_required" className="text-sm font-semibold text-orange-900 cursor-pointer">
                Photos Required
              </Label>
              <p className="text-xs text-orange-700 mt-1">
                User must upload at least one photo before completing this job
              </p>
            </div>
            <Camera className="w-6 h-6 text-[#FFA000]" />
          </div>


          <div className="flex items-center space-x-3 p-4 bg-[#0F1923] rounded-lg" style={{ border: '1px solid rgba(0,200,150,0.35)' }}>
            <input
              type="checkbox"
              id="billable"
              data-testid="create-job-billable-toggle"
              checked={newJob.billable !== false}
              onChange={(e) => setNewJob({ ...newJob, billable: e.target.checked })}
              className="w-5 h-5 rounded border-[#2D3748]"
              style={{ accentColor: '#00C896' }}
            />
            <div className="flex-1">
              <Label htmlFor="billable" className="text-sm font-semibold cursor-pointer" style={{ color: '#6EE7B7' }}>
                Billable job
              </Label>
              <p className="text-xs mt-1" style={{ color: '#64748B' }}>
                Untick for internal or warranty work. Non-billable hours count as overhead in the Service Rate Calculator.
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-4 bg-[#0F1923] rounded-lg" style={{ border: '1px solid rgba(139,92,246,0.35)' }}>
            <input
              type="checkbox"
              id="is_extended"
              data-testid="create-job-extended-toggle"
              checked={newJob.is_extended}
              onChange={(e) => setNewJob({ ...newJob, is_extended: e.target.checked })}
              className="w-5 h-5 rounded border-[#2D3748] focus:ring-purple-500"
              style={{ accentColor: '#8B5CF6' }}
            />
            <div className="flex-1">
              <Label htmlFor="is_extended" className="text-sm font-semibold cursor-pointer" style={{ color: '#C4B5FD' }}>
                Extended Job (Multi-Day)
              </Label>
              <p className="text-xs mt-1" style={{ color: '#A78BFA' }}>
                Workers on this job will NOT be auto-clocked-out at the end of their shift. Shows a purple badge in job lists.
              </p>
            </div>
            <Clock className="w-6 h-6" style={{ color: '#8B5CF6' }} />
          </div>
        </form>
        <div className="flex space-x-3 pt-4 border-t mt-4 flex-shrink-0 bg-[#1A2433] sticky bottom-0">
          <Button
            type="submit"
            form="add-job-form"
            disabled={isLoading}
            className="flex-1 bg-[#1E88E5] hover:bg-blue-700 h-11"
          >
            {isLoading ? 'Creating...' : 'Create Job'}
          </Button>
          <Button
            type="button"
            onClick={() => onOpenChange(false)}
            variant="outline"
            className="flex-1 h-11"
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
